import {
  type Branding,
  DOC_LANGS,
  formatDateRegional,
  formatNumberRegional,
  LOGO_MAX_CHARS,
  type Regional,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const LANG_TITLES: Record<(typeof DOC_LANGS)[number], string> = {
  ru: 'Русский',
  kk: 'Қазақша',
  en: 'English',
};
const TIMEZONES = [
  'Asia/Almaty',
  'Asia/Aqtobe',
  'Asia/Tashkent',
  'Europe/Moscow',
  'Europe/Berlin',
  'Europe/London',
  'Asia/Dubai',
  'America/New_York',
  'UTC',
];
const LOGO_KB = 150;

function fieldErrors(e: unknown): Record<string, string> {
  return (e as { fieldErrors?: () => Record<string, string> } | null)?.fieldErrors?.() ?? {};
}

/** Уменьшает картинку через canvas, пока она не уложится в 150 КБ; прозрачность сохраняется в PNG и WebP */
async function shrinkLogo(file: File): Promise<string> {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new Error('Логотип — PNG, JPEG или WebP');
  const bmp = await createImageBitmap(file);
  const limit = LOGO_KB * 1024;
  let max = 512;
  for (let step = 0; step < 6; step++) {
    const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(bmp.width * k));
    c.height = Math.max(1, Math.round(bmp.height * k));
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('Браузер не смог обработать картинку');
    ctx.drawImage(bmp, 0, 0, c.width, c.height);
    const variants =
      file.type === 'image/jpeg'
        ? [['image/jpeg', 0.85]]
        : [
            ['image/png', undefined],
            ['image/webp', 0.8],
          ];
    for (const [type, q] of variants) {
      const url = c.toDataURL(type as string, q as number | undefined);
      if (url.startsWith(`data:${type}`) && url.length <= LOGO_MAX_CHARS && (url.length * 3) / 4 <= limit)
        return url;
    }
    max = Math.round(max * 0.7);
  }
  throw new Error(`Не удалось уменьшить логотип до ${LOGO_KB} КБ — возьмите картинку проще`);
}

function BrandForm() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['company', 'brand'], queryFn: () => api<Branding>('/company/brand') });
  const [f, setF] = useState<Branding | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (q.data) setF(q.data);
  }, [q.data]);
  const save = useMutation({
    mutationFn: (b: Branding) => api<Branding>('/admin/branding', { method: 'PUT', body: b }),
    onSuccess: (b) => {
      toast('Оформление сохранено');
      qc.setQueryData(['company', 'brand'], b);
    },
  });
  if (q.isPending || !f) return q.isError ? <ErrorBox error={q.error} /> : <Spinner />;
  const errs = fieldErrors(save.error);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    setLogoError(null);
    try {
      setF({ ...f, logo: await shrinkLogo(file) });
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : 'Не удалось загрузить логотип');
    }
    if (fileRef.current) fileRef.current.value = '';
  };
  const color = (k: 'primary' | 'accent', id: string, label: string) => (
    <Field label={label} htmlFor={id} error={errs[k]} hint="Пусто — цвет портала по умолчанию">
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={label}
          value={f[k] ?? '#000000'}
          onChange={(e) => setF({ ...f, [k]: e.target.value })}
          className="h-11 w-14 shrink-0 cursor-pointer rounded-md border border-line-strong bg-surface p-1 md:h-10"
        />
        <Input
          id={id}
          value={f[k] ?? ''}
          placeholder="#RRGGBB"
          maxLength={7}
          onChange={(e) => setF({ ...f, [k]: e.target.value.trim() || null })}
        />
      </div>
    </Field>
  );
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(f);
      }}
    >
      <Field
        label="Название в шапке, на входе и в письмах"
        htmlFor="br-title"
        error={errs.title}
        hint="Пусто — название компании"
      >
        <Input
          id="br-title"
          value={f.title}
          maxLength={80}
          onChange={(e) => setF({ ...f, title: e.target.value })}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        {color('primary', 'br-primary', 'Основной цвет')}
        {color('accent', 'br-accent', 'Акцентный цвет')}
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-[13px] font-medium text-ink">Логотип</span>
        <div className="flex flex-wrap items-center gap-3">
          {f.logo ? (
            <img
              src={f.logo}
              alt="Логотип"
              className="max-h-16 max-w-[200px] rounded-md border border-line bg-surface-2 p-1"
            />
          ) : (
            <span className="text-[13px] text-ink-2">Логотип не задан</span>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            id="br-logo"
            onChange={(e) => pick(e.target.files?.[0])}
          />
          <Button onClick={() => fileRef.current?.click()}>Выбрать файл</Button>
          {f.logo ? <Button onClick={() => setF({ ...f, logo: null })}>Убрать</Button> : null}
        </div>
        <p className="text-[12px] text-ink-2">
          PNG, JPEG или WebP. Большую картинку портал сам уменьшит до {LOGO_KB} КБ.
        </p>
        {logoError ? <ErrorBox error={new Error(logoError)} /> : null}
        {errs.logo ? <ErrorBox error={new Error(errs.logo)} /> : null}
      </div>
      <Field label="Подпись в конце писем" htmlFor="br-footer" error={errs.emailFooter}>
        <Input
          id="br-footer"
          value={f.emailFooter}
          maxLength={300}
          onChange={(e) => setF({ ...f, emailFooter: e.target.value })}
        />
      </Field>
      {save.isError ? <ErrorBox error={save.error} /> : null}
      <div>
        <Button type="submit" variant="primary" loading={save.isPending}>
          Сохранить оформление
        </Button>
      </div>
    </form>
  );
}

function RegionalForm() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['company', 'regional'],
    queryFn: () => api<Regional>('/company/regional'),
  });
  const [f, setF] = useState<Regional | null>(null);
  useEffect(() => {
    if (q.data) setF(q.data);
  }, [q.data]);
  const save = useMutation({
    mutationFn: (r: Regional) => api<Regional>('/admin/regional', { method: 'PUT', body: r }),
    onSuccess: (r) => {
      toast('Региональные настройки сохранены');
      qc.setQueryData(['company', 'regional'], r);
    },
  });
  if (q.isPending || !f) return q.isError ? <ErrorBox error={q.error} /> : <Spinner />;
  const errs = fieldErrors(save.error);
  const set = <K extends keyof Regional>(k: K, v: Regional[K]) => setF({ ...f, [k]: v });
  const zones = TIMEZONES.includes(f.timezone) ? TIMEZONES : [f.timezone, ...TIMEZONES];
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(f);
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Язык документов" htmlFor="rg-lang" error={errs.lang}>
          <Select
            id="rg-lang"
            value={f.lang}
            onChange={(e) => set('lang', e.target.value as Regional['lang'])}
          >
            {DOC_LANGS.map((l) => (
              <option key={l} value={l}>
                {LANG_TITLES[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Часовой пояс" htmlFor="rg-tz" error={errs.timezone}>
          <Select id="rg-tz" value={f.timezone} onChange={(e) => set('timezone', e.target.value)}>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Валюта (код из 3 букв)"
          htmlFor="rg-cur"
          error={errs.currency}
          hint="Например KZT, USD, EUR"
        >
          <Input
            id="rg-cur"
            value={f.currency}
            maxLength={3}
            onChange={(e) => set('currency', e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Формат даты" htmlFor="rg-date" error={errs.dateFormat}>
          <Select
            id="rg-date"
            value={f.dateFormat}
            onChange={(e) => set('dateFormat', e.target.value as Regional['dateFormat'])}
          >
            <option value="dmy">31.12.2026</option>
            <option value="mdy">12/31/2026</option>
            <option value="ymd">2026-12-31</option>
          </Select>
        </Field>
        <Field label="Формат чисел" htmlFor="rg-num" error={errs.numberFormat}>
          <Select
            id="rg-num"
            value={f.numberFormat}
            onChange={(e) => set('numberFormat', e.target.value as Regional['numberFormat'])}
          >
            <option value="space_comma">1 234,56</option>
            <option value="comma_dot">1,234.56</option>
          </Select>
        </Field>
        <Field label="Меры" htmlFor="rg-units" error={errs.units}>
          <Select
            id="rg-units"
            value={f.units}
            onChange={(e) => set('units', e.target.value as Regional['units'])}
          >
            <option value="metric">Метрические (м, мм, л)</option>
            <option value="imperial">Футы, дюймы, галлоны</option>
          </Select>
        </Field>
        <Field label="Вид телефона" htmlFor="rg-phone" error={errs.phoneFormat} hint="Решётка — цифра номера">
          <Input
            id="rg-phone"
            value={f.phoneFormat}
            maxLength={40}
            onChange={(e) => set('phoneFormat', e.target.value)}
          />
        </Field>
      </div>
      <p className="rounded-md bg-surface-2 px-3 py-2.5 text-[13px] text-ink-2">
        Пример: {formatDateRegional('2026-12-31', f.dateFormat)},{' '}
        {formatNumberRegional(1234567.89, f.numberFormat)} {f.currency}
      </p>
      {save.isError ? <ErrorBox error={save.error} /> : null}
      <div>
        <Button type="submit" variant="primary" loading={save.isPending}>
          Сохранить настройки
        </Button>
      </div>
    </form>
  );
}

/** Администрирование → Оформление (ЛЦ-08, МР-05): название, цвета, логотип и региональные форматы */
export function BrandingPage() {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Panel title="Оформление портала" meta="ЛЦ-08">
        <BrandForm />
      </Panel>
      <Panel title="Региональные настройки" meta="МР-05">
        <RegionalForm />
      </Panel>
    </div>
  );
}
